// utils/dateUtils.ts

/**
 * Format a flight duration "HH:MM:SS" (or "HH:MM") into "Hh Mm" — e.g.
 * "02:20:00" → "2h 20m" — matching how durations render on the flight search
 * pages. Hours drop any leading zero; minutes keep their two digits. Returns the
 * original value for anything that isn't a colon-separated time, "" when empty.
 */
export function formatHmsDuration(value: string | null | undefined): string {
  if (!value) return "";
  const parts = value.split(":");
  if (parts.length < 2) return value;
  const hours = parseInt(parts[0], 10);
  if (Number.isNaN(hours)) return value;
  const minutes = parts[1].padStart(2, "0").slice(0, 2);
  return `${hours}h ${minutes}m`;
}

export function getTimeAgo(dateString: string | null | undefined): string {
  if (!dateString) return "";

  const date = new Date(dateString);
  const now = new Date();

  // Check if date is valid
  if (isNaN(date.getTime())) return "";

  const seconds: number = Math.floor((now.getTime() - date.getTime()) / 1000);
  const minutes: number = Math.floor(seconds / 60);
  const hours: number = Math.floor(minutes / 60);
  const days: number = Math.floor(hours / 24);

  if (days > 0) {
    return days === 1 ? "1 day ago" : `${days} days ago`;
  } else if (hours > 0) {
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  } else if (minutes > 0) {
    return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  } else {
    return seconds <= 1 ? "just now" : `${seconds} seconds ago`;
  }
}

export function formatDateTime(
  dateString: string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }
): string {
  if (!dateString) return "";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat(undefined, options).format(date);
}